import React, { useEffect, useState } from 'react';

import { Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import type { LensFilter, LensFilterValues } from '~/types/leLens';

export function reconcileDeclaredFilters(filters: Array<LensFilter>, values: LensFilterValues): LensFilterValues {
  const reconciled = { ...values };
  for (const filter of filters) {
    if (!(filter.id in values)) continue;
    const value = reconcileDeclaredValue(filter, values[filter.id]);
    if (value === undefined) delete reconciled[filter.id];
    else reconciled[filter.id] = value;
  }
  return reconciled;
}

export function effectiveDeclaredFilters(filters: Array<LensFilter>, overrides: LensFilterValues): LensFilterValues {
  return {
    ...reconcileDeclaredFilters(
      filters,
      Object.fromEntries(
        filters.filter((filter) => filter.defaultValue !== undefined).map((filter) => [filter.id, filter.defaultValue]),
      ),
    ),
    ...reconcileDeclaredFilters(filters, overrides),
  };
}

function reconcileDeclaredValue(filter: LensFilter, value: unknown): LensFilterValues[string] | undefined {
  const record =
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)
      ? (value as Record<string, unknown>)
      : undefined;
  const clear =
    filter.type === 'numberRange' || filter.type === 'dateRange'
      ? {}
      : filter.type === 'select' && filter.multiple
        ? []
        : '';
  if (
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (record && Object.keys(record).length === 0)
  )
    return clear;
  if (filter.type === 'numberRange' && record) {
    if (Object.keys(record).some((key) => key !== 'min' && key !== 'max')) return undefined;
    const range: { min?: number; max?: number } = {};
    for (const bound of ['min', 'max'] as const) {
      const next = record[bound];
      if (next === undefined) continue;
      if (typeof next !== 'number' || !Number.isFinite(next)) return undefined;
      range[bound] = next;
    }
    return range.min !== undefined && range.max !== undefined && range.min > range.max ? undefined : range;
  }
  if (filter.type === 'dateRange' && record) {
    if (Object.keys(record).some((key) => key !== 'from' && key !== 'to')) return undefined;
    const range: { from?: string; to?: string } = {};
    for (const bound of ['from', 'to'] as const) {
      const next = record[bound];
      if (next === undefined || next === null || next === '') continue;
      if (!isDeclaredDate(next)) return undefined;
      range[bound] = next;
    }
    return range.from && range.to && range.from > range.to ? undefined : range;
  }
  if (filter.type === 'select' && Array.isArray(value) && value.every((entry) => typeof entry === 'string')) {
    const unique = [...new Set(value)];
    return filter.multiple ? value : unique.length === 1 ? unique[0] : undefined;
  }
  const unique = Array.isArray(value) ? [...new Set(value)] : undefined;
  let scalar = unique ? (unique.length === 1 ? unique[0] : undefined) : value;
  if (scalar === null || scalar === '') return clear;
  if (filter.type === 'boolean') {
    if (typeof scalar === 'boolean') return scalar;
    return scalar === 'true' ? true : scalar === 'false' ? false : undefined;
  }
  if (filter.type === 'select') {
    if (typeof scalar === 'boolean') scalar = String(scalar);
    if (
      record &&
      Object.keys(record).every((key) => key === 'min' || key === 'max') &&
      typeof record.min === 'number' &&
      Number.isFinite(record.min) &&
      record.min === record.max
    )
      scalar = String(record.min);
    if (
      record &&
      Object.keys(record).every((key) => key === 'from' || key === 'to') &&
      isDeclaredDate(record.from) &&
      record.from === record.to
    )
      scalar = record.from;
    return typeof scalar === 'string' ? (filter.multiple ? [scalar] : scalar) : undefined;
  }
  if (filter.type === 'numberRange') {
    const text = typeof scalar === 'string' ? scalar.trim().replace(/(?:\.0+|(\.\d*?[1-9])0+)$/, '$1') : undefined;
    const point =
      typeof scalar === 'number'
        ? scalar
        : text && /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:e[+-]?\d+)?$/i.test(text)
          ? Number(text)
          : Number.NaN;
    return Number.isFinite(point) && (text === undefined || String(point) === text)
      ? { min: point, max: point }
      : undefined;
  }
  return isDeclaredDate(scalar) ? { from: scalar, to: scalar } : undefined;
}

function isDeclaredDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function clearDeclaredFilters(values: LensFilterValues, filters: Array<LensFilter>): LensFilterValues {
  return {
    ...values,
    ...Object.fromEntries(
      filters.map((filter) => [
        filter.id,
        filter.type === 'dateRange' || filter.type === 'numberRange'
          ? {}
          : filter.type === 'select' && filter.multiple
            ? []
            : '',
      ]),
    ),
  };
}

export function isActiveDeclaredValue(value: LensFilterValues[string] | undefined): boolean {
  if (typeof value === 'boolean') return true;
  if (typeof value === 'string') return Boolean(value);
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(value && Object.values(value).some((bound) => bound !== undefined && bound !== ''));
}

export default function NativeLensFilter({
  filter,
  value,
  onChange,
  onValidityChange,
}: {
  filter: LensFilter;
  value: LensFilterValues[string] | undefined;
  onChange: (value: LensFilterValues[string]) => void;
  onValidityChange?: (valid: boolean) => void;
}) {
  if (filter.type === 'boolean')
    return (
      <Stack spacing={1}>
        <Typography variant="body2">{filter.label}</Typography>
        <ToggleButtonGroup
          exclusive
          value={typeof value === 'boolean' ? String(value) : 'all'}
          onChange={(_event, next) => {
            if (next !== null) onChange(next === 'all' ? '' : next === 'true');
          }}
          size="small"
          aria-label={filter.label}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="true">Yes</ToggleButton>
          <ToggleButton value="false">No</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    );
  if (filter.type === 'dateRange') return <DateLensFilter filter={filter} value={value} onChange={onChange} />;
  return <NumericLensFilter filter={filter} value={value} onChange={onChange} onValidityChange={onValidityChange} />;
}

function dateRangeValue(value: LensFilterValues[string] | undefined): { from?: string; to?: string } {
  return value && typeof value === 'object' && !Array.isArray(value) && ('from' in value || 'to' in value)
    ? (value as { from?: string; to?: string })
    : {};
}

function DateLensFilter({
  filter,
  value,
  onChange,
}: {
  filter: LensFilter;
  value: LensFilterValues[string] | undefined;
  onChange: (value: LensFilterValues[string]) => void;
}) {
  const [range, setRange] = useState(() => dateRangeValue(value));
  useEffect(() => setRange(dateRangeValue(value)), [value]);
  const valid = (next: { from?: string; to?: string }) =>
    (!next.from || isDeclaredDate(next.from)) &&
    (!next.to || isDeclaredDate(next.to)) &&
    (!next.from || !next.to || next.from <= next.to);
  return (
    <Stack direction="row" flexWrap="wrap" spacing={1}>
      {(['from', 'to'] as const).map((bound) => (
        <TextField
          key={bound}
          InputLabelProps={{ shrink: true }}
          label={bound === 'from' ? `${filter.label} from` : 'to'}
          type="date"
          value={range[bound] ?? ''}
          error={!valid(range)}
          sx={{ minWidth: 172 }}
          onChange={(event) => {
            const next = { ...range };
            if (event.target.value === '') delete next[bound];
            else next[bound] = event.target.value;
            setRange(next);
            if (valid(next)) onChange(next);
          }}
        />
      ))}
    </Stack>
  );
}

function numberRangeValue(value: LensFilterValues[string] | undefined): { min?: number; max?: number } {
  return value && typeof value === 'object' && !Array.isArray(value) && ('min' in value || 'max' in value)
    ? (value as { min?: number; max?: number })
    : {};
}

function NumericLensFilter({
  filter,
  value,
  onChange,
  onValidityChange,
}: {
  filter: LensFilter;
  value: LensFilterValues[string] | undefined;
  onChange: (value: LensFilterValues[string]) => void;
  onValidityChange?: (valid: boolean) => void;
}) {
  const [range, setRange] = useState(() => numberRangeValue(value));
  useEffect(() => setRange(numberRangeValue(value)), [value]);
  const reversed = range.min !== undefined && range.max !== undefined && range.min > range.max;
  useEffect(() => onValidityChange?.(!reversed), [reversed, onValidityChange]);
  return (
    <Stack direction="row" spacing={1}>
      {(['min', 'max'] as const).map((bound) => (
        <TextField
          key={bound}
          label={`${filter.label} ${bound}`}
          type="number"
          size="small"
          value={range[bound] ?? ''}
          error={reversed}
          helperText={reversed && bound === 'min' ? 'Minimum must not exceed maximum' : undefined}
          onChange={(event) => {
            const next = { ...range };
            if (event.target.value === '') delete next[bound];
            else if (Number.isFinite(Number(event.target.value))) next[bound] = Number(event.target.value);
            setRange(next);
            if (next.min === undefined || next.max === undefined || next.min <= next.max) onChange(next);
          }}
        />
      ))}
    </Stack>
  );
}
